<script setup lang="ts">
import AvatarIcon from '@/uiKit/AvatarIcon.vue'

import { useCaseFilePreview } from '@/modules/Cases/Sidebar/useCaseFilePreview'
import { useCaseStore } from '@/modules/Cases/useCaseStore'
import { useProfileImage } from '@/modules/WorkspaceSettings/useProfileImage'
import { invariant, nonNullable } from '@/shared/utils/typeAssertions'
import IconButton from '@/uiKit/IconButton.vue'
import ToolTip from '@/uiKit/ToolTip.vue'
import CaseAttachment from './CaseAttachment.vue'
import CaseMarkdown from './CaseMarkdown.vue'
import { isFullCase, type BubbleMessageQuery, type CaseInputAttachment } from './types'

const props = defineProps<{ message: BubbleMessageQuery; disabled: boolean }>()

defineEmits<{ 'delete-attachment': [string]; 'delete-query': []; 'edit-query': [] }>()

const profileImageUrl = useProfileImage(props.message.user?.id ?? '').value

const caseStore = useCaseStore()
const { openFilePreview } = useCaseFilePreview()
const onClickAttachment = (attachment: CaseInputAttachment) => {
  const activeCase = caseStore.activeCase
  invariant(isFullCase(activeCase), 'Active case is a partial case')

  if (attachment.type === 'file') {
    const file = nonNullable(activeCase.files.find((file) => file.id === attachment.fileId))
    openFilePreview(file)
  }
}
</script>

<template>
  <div class="group flex flex-col gap-1 pl-2">
    <div
      class="relative flex w-auto max-w-full shrink-0 items-start gap-2 self-start rounded-[18px] bg-background-gray-subtlest py-2 pl-2"
      :class="[
        message.messageType === 'pending_query' && 'opacity-50',
        message.text ? 'pr-4' : 'pr-2',
      ]"
    >
      <AvatarIcon
        class="shrink-0"
        :full-text="message.user?.fullName || message.user?.email || ''"
        shape="circle"
        size="sm"
        :url="profileImageUrl"
      />

      <CaseMarkdown
        v-if="message.text"
        class="text-text-subtle"
        :markdown="message.text"
      />

      <div
        class="pointer-events-none absolute top-0 right-0 flex size-0 h-9 items-center justify-end gap-1 overflow-hidden rounded-tr-[18px] transition-all group-hover:pointer-events-auto group-hover:-right-7 group-hover:w-7 focus-within:pointer-events-auto focus-within:-right-7 focus-within:w-8 focus-within:pr-1"
      >
        <ToolTip
          v-if="message.messageType === 'pending_query'"
          title="Cancel message"
        >
          <IconButton
            icon="trash"
            variant="transparent"
            size="md"
            rounded
            aria-label="Cancel message"
            @click="$emit('delete-query')"
          />
        </ToolTip>

        <ToolTip
          v-else
          title="Edit message"
        >
          <IconButton
            icon="task"
            variant="transparent"
            size="md"
            rounded
            aria-label="Edit message"
            :disabled="disabled"
            @click="$emit('edit-query')"
          />
        </ToolTip>
      </div>
    </div>

    <div
      v-if="message.attachments.length > 0"
      class="flex flex-wrap gap-1"
      role="listitem"
      aria-label="Attachments"
    >
      <CaseAttachment
        v-for="attachment in message.attachments"
        :key="attachment.uploadId"
        :name="attachment.name"
        :progress="attachment.progress"
        :status="attachment.status"
        :deletable="message.messageType === 'pending_query'"
        :interactive="message.messageType === 'query'"
        @delete="$emit('delete-attachment', attachment.uploadId)"
        @click="message.messageType === 'query' && onClickAttachment(attachment)"
      />
      <CaseAttachment
        v-if="message.attachments.length > 4"
        :name="`+${message.attachments.length - 4}`"
        :progress="null"
      />
    </div>
  </div>
</template>
